import { useState } from "react";
import { ShieldCheck, ShieldOff, Loader2, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

/**
 * Two-factor authentication panel for the Account page.
 *
 * Flow:
 *   1) User taps "Enable" → POST /auth/mfa/enroll → server returns secret + otpauth_uri
 *   2) Show QR (via api.qrserver.com — no extra library) + manual secret
 *   3) User scans in Google Authenticator/1Password/Authy, enters 6-digit code
 *   4) POST /auth/mfa/verify → mfa_enabled=true
 *
 * Disable: requires current password.
 */
const QR_PROVIDER = (uri) => `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(uri)}`;

export default function MfaSection({ enabled, onChanged }) {
  const [stage, setStage] = useState("idle"); // idle | enrolling | verifying | disabling
  const [secret, setSecret] = useState("");
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/mfa/enroll");
      setSecret(res.data.secret);
      setUri(res.data.otpauth_uri);
      setStage("verifying");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start setup.");
    } finally { setBusy(false); }
  };

  const confirmEnroll = async (e) => {
    e?.preventDefault?.();
    if (code.replace(/\s/g, "").length !== 6) return;
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify", { code: code.trim() });
      toast.success("Two-factor authentication enabled.");
      setStage("idle"); setSecret(""); setUri(""); setCode("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "That code didn't match.");
    } finally { setBusy(false); }
  };

  const disable = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: pw });
      toast.success("Two-factor authentication disabled.");
      setStage("idle"); setPw("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't disable.");
    } finally { setBusy(false); }
  };

  const copySecret = () => {
    navigator.clipboard?.writeText(secret).then(() => toast.success("Secret copied."));
  };

  if (stage === "verifying") {
    return (
      <div className="space-y-4">
        <p className="text-sm text-[#C7DAD3] leading-relaxed">
          Scan this QR code with Google Authenticator, 1Password, Authy, or any TOTP app — then enter the 6-digit code it shows.
        </p>
        <div className="flex items-center justify-center bg-white rounded-2xl p-3 mx-auto w-[200px]">
          <img src={QR_PROVIDER(uri)} alt="MFA QR code" width={180} height={180} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#7B948B] font-bold mb-1">Can't scan? Enter this key manually:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[12px] text-amber-300 bg-[#0A2320] border border-[#1B4238] rounded-lg px-2.5 py-2 tracking-widest break-all">{secret}</code>
            <button type="button" onClick={copySecret} className="h-9 w-9 rounded-lg border border-[#1B4238] text-[#C7DAD3] hover:bg-[#11413B] flex items-center justify-center" aria-label="Copy">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
        <form onSubmit={confirmEnroll} className="space-y-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            placeholder="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="h-11 text-center tracking-[0.5em] text-lg bg-[#0A2320] border-[#1B4238] text-white"
            data-testid="mfa-enroll-code"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setStage("idle"); setSecret(""); setCode(""); }}
              className="flex-1 h-11 rounded-xl border border-[#1B4238] text-[#C7DAD3] hover:bg-[#11413B]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || code.length !== 6}
              className="flex-1 h-11 rounded-xl bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] font-black uppercase tracking-wider"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="mfa-enroll-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Check className="w-4 h-4 mr-1.5" /> Turn on</>)}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  if (enabled) {
    return (
      <form onSubmit={disable} className="space-y-3">
        <div className="flex items-center gap-2.5 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
          <ShieldCheck className="w-5 h-5 text-emerald-400" strokeWidth={2.4} />
          <div>
            <p className="text-emerald-300 text-sm font-bold leading-tight">MFA is on</p>
            <p className="text-[11px] text-[#7B948B] mt-0.5">A 6-digit code is required every time you sign in.</p>
          </div>
        </div>
        <p className="text-[12px] text-[#94B0A6]">Enter your current password to disable.</p>
        <Input
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          type="password"
          placeholder="Current password"
          className="h-10 bg-[#0A2320] border-[#1B4238] text-white"
          data-testid="mfa-disable-pw"
        />
        <Button
          type="submit"
          disabled={busy || !pw}
          variant="ghost"
          className="w-full h-10 rounded-xl border border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10"
          data-testid="mfa-disable-confirm"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><ShieldOff className="w-4 h-4 mr-1.5" /> Turn off MFA</>)}
        </Button>
      </form>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-[#C7DAD3] leading-relaxed">
        Add a second sign-in step using an authenticator app. Plaid and other finance integrations recommend this.
      </p>
      <Button
        onClick={startEnroll}
        disabled={busy}
        className="w-full h-11 rounded-xl bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] font-black uppercase tracking-wider"
        style={{ fontFamily: "Outfit, sans-serif" }}
        data-testid="mfa-enable-start"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><ShieldCheck className="w-4 h-4 mr-1.5" /> Enable 2FA</>)}
      </Button>
    </div>
  );
}
